<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <div class="rd-seg" role="tablist" :aria-label="$t('view')">
        <button type="button" role="tab" :aria-selected="String(type === 'orders')" @click="type = 'orders'">
          {{ $t("orders") }}
        </button>
        <button type="button" role="tab" :aria-selected="String(type === 'foods')" @click="type = 'foods'">
          {{ $t("meals_main") }}
        </button>
      </div>
    </report-tools>

    <report-state v-if="loading && !reportItem.length" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-truck-delivery-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-card">
      <table class="rd-tbl">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("firm") }} {{ sortArrow("name") }}</button>
            </th>
            <th v-for="shift in visibleShifts" :key="shift.id" scope="col" class="is-right">
              <report-shift-tag :shift="shift.id"></report-shift-tag>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('ordered')">
              <button type="button" @click="sortBy('ordered')">{{ $t("total") }} {{ sortArrow("ordered") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('difference')">
              <button type="button" @click="sortBy('difference')">
                {{ $t("difference") }} {{ sortArrow("difference") }}
              </button>
            </th>
            <th scope="col" class="is-right rd-col-actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.id">
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="row.name">{{ row.name }}</td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-right">
              <span
                v-if="row[shift.key].ordered"
                class="rd-cellbar"
                :class="{ 'is-complete': row[shift.key].complete }"
              >
                <span class="rd-cellbar__text">{{ row[shift.key].ordered }} / {{ row[shift.key].taken }}</span>
                <span class="rd-cellbar__bar"><i :style="{ width: `${row[shift.key].percent}%` }"></i></span>
              </span>
              <span v-else class="is-zero">–</span>
            </td>
            <td class="is-right rd-num">
              <b>{{ row.ordered }}</b> / {{ row.taken }}
              <span class="rd-mini" :class="{ 'is-complete': row.complete }">
                <i :style="{ width: `${row.percent}%` }"></i>
              </span>
            </td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.difference, 'is-alert': row.difference > 0 }">
              {{ row.difference }}
            </td>
            <td class="is-right">
              <div class="rd-tbl__actions">
                <button type="button" class="rd-btn rd-btn--sm" :aria-label="$t('employees')" @click="openCompanyEmployees(row.source)">
                  <v-icon size="16">mdi-account-multiple-outline</v-icon>
                  <span class="rd-btn__label">{{ $t("employees") }}</span>
                </button>
                <button
                  type="button"
                  class="rd-btn rd-btn--sm"
                  :aria-label="$t('monthly')"
                  @click="openDialogReportFirm(row.source)"
                >
                  <v-icon size="16">mdi-calendar-month-outline</v-icon>
                  <span class="rd-btn__label">{{ $t("monthly") }}</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">
              {{ summary[shift.key].ordered ? `${summary[shift.key].ordered} / ${summary[shift.key].taken}` : "–" }}
            </td>
            <td class="is-right rd-num">{{ summary.ordered }} / {{ summary.taken }}</td>
            <td class="is-right rd-num">{{ summary.difference }}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <report-group-meals-taken-company-monthly-orders
      v-model="dialogFirm"
      :firm-report-item="firmReportItem"
    ></report-group-meals-taken-company-monthly-orders>
  </div>
</template>

<script>
import ReportGroupMealsTakenCompanyMonthlyOrders from "@/components/report/group-meals/dialogs/ReportGroupMealsTakenCompanyMonthlyOrders.vue";
import ReportTabGroupMealsTabTaken from "@/components/report/group-meals/ReportTabGroupMealsTabTaken.vue";
import { SHIFTS } from "@/constants/shifts";
import hasShiftBinding from "../../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../../shared/hasShiftSubtitle";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import hasGroupTakenRows from "../../../shared/group/hasGroupTakenRows";
import { copyText, rowsToTsv } from "../../copyTable";
import hasTableSort from "../../hasTableSort";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";

/**
 * Naručeno / Preuzeto (jela iz grupa) na računaru: firma × smena, naručeno / preuzeto sa trakom napretka, ukupno i
 * razlika. "Narudžbe / Jela" je prekidač u alatima, smene su u zajedničkoj traci. "Radnici" vodi na Korisnike
 * te firme, "Mesečno" otvara mesečni pregled firme (dijalog kao do sada). Nasleđuje učitavanje, štampu i Excel iz
 * starog taba.
 */
export default {
  name: "ReportDesktopGroupTaken",
  extends: ReportTabGroupMealsTabTaken,
  mixins: [hasShiftBinding, hasShiftSubtitle, hasTableSort, hasGroupTakenRows],
  components: { ReportGroupMealsTakenCompanyMonthlyOrders, ReportShiftTag, ReportState, ReportTools },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Smene 1–3 uvek (ako su izabrane), četvrta samo kad neka firma ima obroke u njoj. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.selectedShifts.includes(shift.id) && (shift.id < 4 || this.total.some((item) => item[shift.key]?.quantity > 0))
      );
    },
    rows() {
      return this.sortRows(this.matchedRows);
    },
    summary() {
      return this.summarize(this.rows);
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("no_taken_group_meals_data");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.summary.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.summary.taken },
        { key: "difference", label: this.$t("difference"), value: this.summary.difference },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    /** Radnici firme: ista firma u Korisnicima (isti izbor u store-u kao i u starom tabu). */
    openCompanyEmployees(item) {
      this.selectCompanyForUsers(item);
      this.$emit("open-screen", "groupUsers");
    },
    async copyTable() {
      const headers = [
        this.$t("firm"),
        ...this.visibleShifts.map((shift) => `${this.$t(shift.label)} (${this.$t("ordered_taken_meals")})`),
        `${this.$t("total")} (${this.$t("ordered_taken_meals")})`,
        this.$t("difference"),
      ];
      const rows = this.rows.map((row) => [
        row.name,
        ...this.visibleShifts.map((shift) => `${row[shift.key].ordered} / ${row[shift.key].taken}`),
        `${row.ordered} / ${row.taken}`,
        row.difference,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
